import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "@/app/queries";
import { count } from "@/lib/plural";
import { Button } from "@/ui/Button";

/** Отчёт импорта базы знаний — как у `lmsctl import-kb` (backend/internal/kb.Report). */
export type KbReport = {
  tracks: { created: number; updated: number; unchanged: number };
  steps: { created: number; updated: number; unchanged: number };
  resources: number;
  units: number;
  banks: { name: string; items: number; asked: number }[];
  notes: { created: number; updated: number; unchanged: number };
  extra: string[];
  unresolved: { path: string; line: number; kind: string; text: string }[];
  warnings: { path: string; line: number; kind: string; text: string }[];
  homework_attached: string[];
  homework_skipped: string[];
};

const counts = (c: { created: number; updated: number; unchanged: number }) => `создано ${c.created} · обновлено ${c.updated} · без изменений ${c.unchanged}`;

/** Импорт базы знаний архивом (A1, страница без шага): zip базы как в Obsidian —
 *  Roadmap.md, Roadmap/, Notes/, QA/, QAInterviews/, Homeworks/, lms.json. Всё — черновиками,
 *  публикуется из редактора шага; отчёт тот же, что у CLI. */
export function KbImport() {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<{ kind: "idle" } | { kind: "busy"; name: string } | { kind: "done"; report: KbReport } | { kind: "error"; message: string }>({ kind: "idle" });

  const upload = async (file: File) => {
    setState({ kind: "busy", name: file.name });
    const body = new FormData();
    body.append("file", file);
    try {
      const res = await fetch("/api/admin/kb/import", { method: "POST", body });
      const data = (await res.json().catch(() => null)) as (KbReport & { message?: string }) | null;
      if (!res.ok || !data) {
        setState({ kind: "error", message: data?.message ?? `Импорт не удался (${res.status}).` });
        return;
      }
      setState({ kind: "done", report: data });
      void qc.invalidateQueries({ queryKey: keys.tree });
      void qc.invalidateQueries({ queryKey: ["admin"] });
    } catch {
      setState({ kind: "error", message: "Нет связи с сервером — архив не отправился, попробуй ещё раз." });
    }
  };

  return (
    <section className="rounded-md border border-bd bg-card" data-testid="kb-import">
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-4 py-3">
        <span className="font-mono text-[11px] uppercase tracking-[.06em] text-mut">база знаний</span>
        <span className="text-[13px] text-ink">Импорт из Obsidian-базы архивом</span>
        <span className="flex-1" />
        <Button size="sm" variant="primary" onClick={() => input.current?.click()} disabled={state.kind === "busy"} reason={state.kind === "busy" ? `импортируем ${state.name}…` : undefined} data-testid="kb-import-pick">
          выбрать zip
        </Button>
        <input
          ref={input}
          type="file"
          accept=".zip,application/zip"
          className="hidden"
          aria-label="архив базы знаний"
          data-testid="kb-import-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = "";
          }}
        />
      </div>
      <div className="px-4 py-3 text-[12.5px] leading-[1.55] text-ink2">
        Zip всей базы: <code className="font-mono text-ink">Roadmap.md</code>, <code className="font-mono text-ink">Roadmap/</code>, <code className="font-mono text-ink">Notes/</code>, <code className="font-mono text-ink">QA/</code>, <code className="font-mono text-ink">QAInterviews/</code>, <code className="font-mono text-ink">Homeworks/</code>, <code className="font-mono text-ink">lms.json</code> (до 64 МБ). Шаги приходят черновиками — публикуй из редактора; правки, сделанные здесь, импорт не стирает. Повторный импорт того же архива ничего не меняет.
      </div>
      {state.kind === "error" && (
        <p className="m-0 border-t border-bd px-4 py-3 font-mono text-[12px] text-err" role="alert" data-testid="kb-import-error">
          {state.message}
        </p>
      )}
      {state.kind === "done" && <Report r={state.report} />}
    </section>
  );
}

function Report({ r }: { r: KbReport }) {
  const questions = r.banks.reduce((n, b) => n + b.items, 0);
  const rows: [string, string][] = [
    ["треки", counts(r.tracks)],
    ["шаги", `${counts(r.steps)} · ${count(r.resources, "ресурс", "ресурса", "ресурсов")}, ${count(r.units, "юнит", "юнита", "юнитов")}`],
    ["банки вопросов", `${count(r.banks.length, "банк", "банка", "банков")} · ${count(questions, "вопрос", "вопроса", "вопросов")}`],
    ["страницы заметок", counts(r.notes)],
  ];
  const lists: [string, string[]][] = [
    ["домашки прикреплены", r.homework_attached],
    ["домашки пропущены", r.homework_skipped],
    ["в LMS есть, в базе нет (оставлено)", r.extra],
    ["неразрешённые ссылки", r.unresolved.map((i) => `${i.path}${i.line ? ":" + i.line : ""} ${i.text}`)],
    ["предупреждения", r.warnings.map((i) => `${i.path}${i.line ? ":" + i.line : ""} ${i.text}`)],
  ];
  return (
    <div className="border-t border-bd px-4 py-3" data-testid="kb-import-report" role="status">
      <div className="mb-2 font-mono text-[11px] uppercase tracking-[.06em] text-ok">импорт выполнен</div>
      <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 font-mono text-[11.5px]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-mut">{k}</dt>
            <dd className="m-0 text-ink2">{v}</dd>
          </div>
        ))}
      </dl>
      {lists
        .filter(([, items]) => items.length > 0)
        .map(([title, items]) => (
          <details key={title} className="mt-2 text-[12px]" open={title.startsWith("неразреш")}>
            <summary className="cursor-pointer font-mono text-[11.5px] text-accdim">
              {title} · {items.length}
            </summary>
            <ul className="m-0 mt-1 flex list-disc flex-col gap-0.5 pl-5 text-ink2">
              {items.map((it, i) => (
                <li key={i}>{it}</li>
              ))}
            </ul>
          </details>
        ))}
    </div>
  );
}
